import type { BonusKey } from '../research/Research';
import type { OwnerId } from '../ids';
import type { BuildingStore } from '../buildings/BuildingStore';
import { buildingDef, statsAt } from '../buildings/definitions';
import type { PlayerId } from '../ids';
import { isPlayerId } from '../ids';
import type { UnitStore } from '../units/UnitStore';

export interface PowerStatus {
  /** Power supplied by complete Generators. */
  readonly supply: number;
  /** Power wanted by complete powered buildings. */
  readonly demand: number;
  /** Power actually used (≤ supply). */
  readonly used: number;
}

export type DefenceEvent =
  /** A turret fired (for a muzzle flash); `at` = target position. */
  | { type: 'turretFired'; buildingId: number; targetId: number; at: { x: number; y: number }; projectile: number }
  /** A player's power balance changed (a building gained or lost power). */
  | { type: 'power'; playerId: PlayerId };

/**
 * Defences (Phase 8, DECISIONS D75–D79): power and turrets. Engine-free and
 * deterministic.
 *
 *  Power  each complete Generator supplies power; complete buildings that use
 *         power are powered in id order while supply lasts (oldest first). An
 *         unpowered building does nothing.
 *  Turret each complete (and, if needed, powered) turret building shoots the
 *         nearest enemy UNIT within range of its footprint every cooldown,
 *         with a projectile (the Phase 6 rules: damage on impact, armour).
 *         Turrets do not shoot buildings.
 */
export class Defence {
  private readonly cooldown = new Map<number, number>();
  private readonly powered = new Set<number>();
  private readonly status = new Map<PlayerId, PowerStatus>();
  private readonly listeners = new Set<(e: DefenceEvent) => void>();
  private readonly unsubscribe: () => void;
  private dirty = true;
  /** D128: research bonuses (set by the Simulation). */
  bonus: (owner: OwnerId, key: BonusKey) => number = (_o, key) => (key.endsWith('_add') ? 0 : 1);

  constructor(
    private readonly buildings: BuildingStore,
    private readonly units: UnitStore,
    private readonly players: readonly PlayerId[],
  ) {
    this.unsubscribe = buildings.subscribe((e) => {
      if (e.type === 'added' || e.type === 'removed' || e.type === 'completed' || e.type === 'upgraded') this.dirty = true;
      if (e.type === 'removed') this.cooldown.delete(e.building.id);
    });
    this.refreshPower();
  }

  subscribe(l: (e: DefenceEvent) => void): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  dispose(): void {
    this.unsubscribe();
    this.listeners.clear();
  }

  power(p: PlayerId): PowerStatus {
    if (this.dirty) this.refreshPower();
    return this.status.get(p) ?? { supply: 0, demand: 0, used: 0 };
  }

  /** Does this building have the power it needs? (true for buildings that need none) */
  isPowered(id: number): boolean {
    if (this.dirty) this.refreshPower();
    const b = this.buildings.get(id);
    if (!b) return false;
    return !buildingDef(b.type).powerUse || this.powered.has(id);
  }

  /** Can this turret shoot right now? (complete, and powered if it needs power) */
  isActive(id: number): boolean {
    const b = this.buildings.get(id);
    return !!b && b.state === 'complete' && !!statsAt(b.type, b.level).turret && this.isPowered(id);
  }

  step(dt: number): void {
    if (!(dt > 0)) return;
    if (this.dirty) this.refreshPower();
    for (const b of this.buildings.all()) {
      const t = statsAt(b.type, b.level).turret;
      if (!t || b.state !== 'complete' || !isPlayerId(b.ownerId)) continue;
      const cd = Math.max(0, (this.cooldown.get(b.id) ?? 0) - dt);
      this.cooldown.set(b.id, cd);
      if (cd > 1e-9 || !this.isPowered(b.id)) continue;
      const fp = buildingDef(b.type).footprint;
      const cx = b.origin.x + (fp.width - 1) / 2;
      const cy = b.origin.y + (fp.height - 1) / 2;
      // Range is measured from the footprint edge: add the half-diagonal of the footprint.
      const reach = t.range + this.bonus(b.ownerId, 'turret_range_add') + Math.max(fp.width, fp.height) / 2;
      const target = this.units.enemiesNear(b.ownerId, cx, cy, reach)[0];
      if (!target) continue;
      this.cooldown.set(b.id, t.cooldown * this.bonus(b.ownerId, 'turret_cooldown'));
      const p = this.units.projectiles.fire({
        attackerId: -b.id,
        ownerId: b.ownerId,
        x: cx,
        y: cy,
        target: { kind: 'unit', id: target.id },
        aim: { x: target.x, y: target.y },
        speed: t.projectileSpeed,
        damage: t.damage,
        damageType: t.damageType,
      });
      this.emit({ type: 'turretFired', buildingId: b.id, targetId: target.id, at: { x: target.x, y: target.y }, projectile: p.id });
    }
  }

  snapshot(): { cooldowns: Record<string, number>; powered: number[] } {
    const cooldowns: Record<string, number> = {};
    for (const k of [...this.cooldown.keys()].sort((a, b) => a - b)) cooldowns[String(k)] = this.cooldown.get(k)!;
    return { cooldowns, powered: [...this.powered].sort((a, b) => a - b) };
  }

  /** Recompute power on the next step (a research changed Generator output). */
  markDirty(): void {
    this.dirty = true;
  }

  private refreshPower(): void {
    this.dirty = false;
    const before = new Set(this.powered);
    this.powered.clear();
    for (const p of this.players) {
      let supply = 0;
      const users: { id: number; use: number }[] = [];
      for (const b of this.buildings.ownedBy(p)) {
        if (b.state !== 'complete') continue;
        supply += statsAt(b.type, b.level).power * this.bonus(b.ownerId, 'power');
        const use = buildingDef(b.type).powerUse ?? 0;
        if (use > 0) users.push({ id: b.id, use });
      }
      let left = supply;
      let demand = 0;
      for (const u of users) {
        demand += u.use;
        if (u.use <= left) {
          left -= u.use;
          this.powered.add(u.id);
        }
      }
      this.status.set(p, { supply, demand, used: supply - left });
    }
    const changed = new Set<PlayerId>();
    for (const id of new Set([...before, ...this.powered])) {
      if (before.has(id) === this.powered.has(id)) continue;
      const o = this.buildings.get(id)?.ownerId;
      if (o && isPlayerId(o)) changed.add(o);
    }
    for (const p of changed) this.emit({ type: 'power', playerId: p });
  }

  private emit(e: DefenceEvent): void {
    for (const l of this.listeners) l(e);
  }
}
